import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Shot, TransitionSpec } from './types';
import { MANUAL_TRANSITION, totalRuntimeMs } from './shots';
import { AnimationRegistry } from './transitions';
import { ImagePreloader } from './preload';
import { keptBefore } from './videoTrim';

/** Opening title card duration before the first memory rises in. */
export const INTRO_MS = 3800;
/** Index used while the opening card is on screen. */
export const INTRO_INDEX = -1;

export type FilmPhase = 'playing' | 'paused' | 'ended';

export interface Layer {
  /** Unique per appearance, so React never reuses (and restarts) a layer. */
  key: string;
  shotIndex: number;
  enter: TransitionSpec;
  /** Set when the layer is being replaced; it animates out, then is removed. */
  leave: TransitionSpec | null;
}

export interface FrameInfo {
  /** Position in the film (excluding the opening card), ms. */
  positionMs: number;
  totalMs: number;
  /** Progress through the current shot, 0..1. */
  shotProgress: number;
  index: number;
}

/** What a shot needs decoded before it may appear (the first URL gates entry). */
function warmUrls(shot: Shot | undefined): string[] {
  if (!shot) return [];
  if (shot.kind === 'video') return shot.item.posterUrl ? [shot.item.posterUrl] : [];
  const urls = [shot.item.displayUrl ?? shot.item.url];
  if (shot.parallax && shot.item.depthUrl) urls.push(shot.item.depthUrl);
  return urls;
}

/**
 * The film's clock and edit state.
 *
 * One requestAnimationFrame loop drives everything. It never calls setState
 * per frame: progress is pushed to `onFrame`, which the UI writes straight
 * into the DOM. React only re-renders when the shot, phase or layers change.
 *
 *   photos -> a virtual clock that only advances while playing
 *   videos -> the <video> element is authoritative; 'ended' advances
 *
 * Navigation waits (briefly) for the next image to be decoded, so a new shot
 * never appears as a blank frame. A sequence number makes a newer navigation
 * supersede an older one that is still waiting.
 */
export function useFilmEngine(opts: {
  shots: Shot[];
  openingCard: boolean;
  /** Opening card duration; longer when it carries a story opening. */
  introMs?: number;
  /**
   * Asked when a shot's time is up (INTRO_INDEX for the opening card): false
   * keeps it on screen, e.g. while its narration is still being spoken.
   */
  canAdvance?: (index: number) => boolean;
  onFrame?: (info: FrameInfo) => void;
}) {
  const { shots } = opts;
  const introMsRef = useRef(opts.introMs ?? INTRO_MS);
  introMsRef.current = opts.introMs ?? INTRO_MS;
  const registry = useMemo(() => new AnimationRegistry(), []);
  const preloader = useMemo(() => new ImagePreloader(), []);

  const [phase, setPhaseState] = useState<FilmPhase>('playing');
  const [index, setIndexState] = useState<number>(opts.openingCard ? INTRO_INDEX : 0);
  const [layers, setLayers] = useState<Layer[]>([]);
  /** The intro card is fading out while shot 0 rises underneath it. */
  const [introLeaving, setIntroLeaving] = useState(false);

  const phaseRef = useRef<FilmPhase>('playing');
  const indexRef = useRef(index);
  const shotsRef = useRef(shots);
  const elapsedRef = useRef(0);
  const waitingRef = useRef(false);
  const navSeq = useRef(0);
  const videos = useRef(new Map<number, HTMLVideoElement>());
  const onFrameRef = useRef(opts.onFrame);
  const canAdvanceRef = useRef(opts.canAdvance);
  canAdvanceRef.current = opts.canAdvance;
  /** The video shot whose clip has ended but whose hold (narration) runs on. */
  const videoDoneRef = useRef<number | null>(null);
  const errorTimer = useRef<number | null>(null);

  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);
  useEffect(() => {
    onFrameRef.current = opts.onFrame;
  });

  const setPhase = useCallback(
    (p: FilmPhase) => {
      phaseRef.current = p;
      setPhaseState(p);
      if (p === 'playing') registry.resume();
      else registry.pause();
    },
    [registry],
  );

  const setIndex = (i: number) => {
    indexRef.current = i;
    setIndexState(i);
  };

  /** Keep decoded images for a small window around the current shot. */
  const warmAround = useCallback(
    (i: number) => {
      const list = shotsRef.current;
      const keep = new Set<string>();
      for (const j of [i - 1, i, i + 1, i + 2]) {
        for (const url of warmUrls(list[j])) {
          keep.add(url);
          void preloader.load(url);
        }
      }
      preloader.retain(keep);
    },
    [preloader],
  );

  const clearErrorTimer = () => {
    if (errorTimer.current !== null) window.clearTimeout(errorTimer.current);
    errorTimer.current = null;
  };

  const finish = useCallback(() => {
    navSeq.current += 1;
    waitingRef.current = false;
    clearErrorTimer();
    setPhase('ended');
  }, [setPhase]);

  /**
   * Move to shot `target`. `manual` navigation uses a short dissolve so the
   * controls feel immediate; while paused it cuts, so the new frame is
   * visible instead of a transition frozen at its first frame.
   */
  const goTo = useCallback(
    async (target: number, mode: 'auto' | 'manual') => {
      const list = shotsRef.current;
      if (target >= list.length) {
        finish();
        return;
      }
      const to = Math.max(0, target);
      const seq = ++navSeq.current;
      waitingRef.current = true;
      clearErrorTimer();

      const urls = warmUrls(list[to]);
      await Promise.all(urls.map((u) => preloader.ready(u, mode === 'manual' ? 700 : 2500)));
      if (seq !== navSeq.current) return; // superseded by a newer navigation

      const paused = phaseRef.current !== 'playing';
      const tx: TransitionSpec =
        mode === 'manual' ? (paused ? { kind: 'cut', ms: 0 } : MANUAL_TRANSITION) : list[to].transitionIn;

      setLayers((prev) => {
        const current = prev.find((l) => !l.leave);
        // Older leaving layers are dropped: at most two layers ever overlap.
        const leaving = current ? [{ ...current, leave: tx }] : [];
        return [...leaving, { key: `${to}:${seq}`, shotIndex: to, enter: tx, leave: null }];
      });
      if (indexRef.current === INTRO_INDEX) setIntroLeaving(true);
      setIndex(to);
      elapsedRef.current = 0;
      videoDoneRef.current = null;
      waitingRef.current = false;
      warmAround(to);
    },
    [finish, preloader, warmAround],
  );

  /** Called by a layer once its exit animation has finished. */
  const removeLayer = useCallback((key: string) => {
    setLayers((prev) => prev.filter((l) => l.key !== key));
  }, []);

  // ---- The clock -------------------------------------------------------------
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      // Real elapsed time, so the clock stays in step with the Web Animations
      // motion even when the browser throttles frames (unfocused windows run
      // rAF at ~1 Hz). Capped at 1 s: a hidden tab pauses the film anyway.
      const dt = Math.min(1000, now - last);
      last = now;
      const list = shotsRef.current;
      const i = indexRef.current;

      if (phaseRef.current === 'playing' && !waitingRef.current) {
        const mayGo = () => canAdvanceRef.current?.(i) ?? true;
        if (i === INTRO_INDEX) {
          elapsedRef.current += dt;
          if (elapsedRef.current >= introMsRef.current && mayGo()) void goTo(0, 'auto');
        } else {
          const shot = list[i];
          if (shot?.kind === 'photo' || (shot?.kind === 'video' && videoDoneRef.current === i)) {
            elapsedRef.current += dt;
            if (elapsedRef.current >= shot.holdMs && mayGo()) void goTo(i + 1, 'auto');
          } else if (shot?.kind === 'video') {
            const v = videos.current.get(i);
            if (v) elapsedRef.current = (shot.item.trim ? keptBefore(shot.item.trim, v.currentTime) : v.currentTime) * 1000;
          }
        }
      }

      if (onFrameRef.current && i !== INTRO_INDEX) {
        let before = 0;
        for (let j = 0; j < i && j < list.length; j++) before += list[j].holdMs;
        const shot = list[i];
        const hold = shot
          ? shot.kind === 'video'
            ? Math.max(shot.holdMs, videoLengthMs(videos.current.get(i), shot))
            : shot.holdMs
          : 1;
        const within = Math.min(elapsedRef.current, hold);
        onFrameRef.current({
          positionMs: phaseRef.current === 'ended' ? totalRuntimeMs(list) : before + within,
          totalMs: totalRuntimeMs(list),
          shotProgress: phaseRef.current === 'ended' ? 1 : hold > 0 ? within / hold : 0,
          index: i,
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [goTo]);

  // Start: warm the first shots, and skip straight in when there is no card.
  useEffect(() => {
    warmAround(0);
    if (!opts.openingCard) void goTo(0, 'auto');
    return () => {
      navSeq.current += 1;
      clearErrorTimer();
      preloader.clear();
    };
    // Mount only: later preference changes must not restart the film.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Controls ---------------------------------------------------------------
  const play = useCallback(() => {
    if (phaseRef.current === 'ended') return;
    setPhase('playing');
  }, [setPhase]);

  const pause = useCallback(() => {
    if (phaseRef.current === 'playing') setPhase('paused');
  }, [setPhase]);

  const togglePlay = useCallback(() => {
    if (phaseRef.current === 'playing') setPhase('paused');
    else if (phaseRef.current === 'paused') setPhase('playing');
  }, [setPhase]);

  const next = useCallback(() => {
    const i = indexRef.current;
    if (phaseRef.current === 'ended') return;
    void goTo(i === INTRO_INDEX ? 0 : i + 1, 'manual');
  }, [goTo]);

  const prev = useCallback(() => {
    const i = indexRef.current;
    if (phaseRef.current === 'ended') {
      setPhase('paused');
      void goTo(shotsRef.current.length - 1, 'manual');
      return;
    }
    void goTo(Math.max(0, i - 1), 'manual');
  }, [goTo, setPhase]);

  const jumpTo = useCallback(
    (target: number) => {
      if (phaseRef.current === 'ended') setPhase('paused');
      void goTo(target, 'manual');
    },
    [goTo, setPhase],
  );

  const skipIntro = useCallback(() => {
    if (indexRef.current === INTRO_INDEX) void goTo(0, 'auto');
  }, [goTo]);

  const restart = useCallback(() => {
    navSeq.current += 1;
    clearErrorTimer();
    setLayers([]);
    setIntroLeaving(false);
    elapsedRef.current = 0;
    videoDoneRef.current = null;
    waitingRef.current = false;
    setPhase('playing');
    if (opts.openingCard) setIndex(INTRO_INDEX);
    else {
      setIndex(0);
      void goTo(0, 'auto');
    }
  }, [goTo, opts.openingCard, setPhase]);

  /** Video elements register so the clock can read their real position. */
  const registerVideo = useCallback((shotIndex: number, el: HTMLVideoElement | null) => {
    if (el) videos.current.set(shotIndex, el);
    else videos.current.delete(shotIndex);
  }, []);

  // The clock moves on once the video's hold (stretched for narration) is over.
  const onVideoEnded = useCallback((shotIndex: number) => {
    if (shotIndex !== indexRef.current) return;
    const v = videos.current.get(shotIndex);
    const trim = shotsRef.current[shotIndex]?.item.trim;
    if (trim) elapsedRef.current = trim.keptSeconds * 1000;
    else if (v && Number.isFinite(v.duration)) elapsedRef.current = v.duration * 1000;
    // Rounding in the stored length must not keep a finished video waiting; only a narration hold does.
    const hold = shotsRef.current[shotIndex]?.holdMs ?? 0;
    if (hold - elapsedRef.current < 100) elapsedRef.current = Math.max(elapsedRef.current, hold);
    videoDoneRef.current = shotIndex;
  }, []);

  /** A memory failed to load: show it briefly, then carry on if playing. */
  const onShotError = useCallback(
    (shotIndex: number) => {
      if (shotIndex !== indexRef.current) return;
      clearErrorTimer();
      // Still waits for its narration: a broken file never cuts a line short.
      const tryNext = () => {
        if (indexRef.current !== shotIndex) return;
        if (phaseRef.current === 'playing' && (canAdvanceRef.current?.(shotIndex) ?? true)) void goTo(shotIndex + 1, 'auto');
        else errorTimer.current = window.setTimeout(tryNext, 300);
      };
      errorTimer.current = window.setTimeout(tryNext, 3000);
    },
    [goTo],
  );

  return {
    phase,
    index,
    layers,
    introLeaving,
    registry,
    play,
    pause,
    togglePlay,
    next,
    prev,
    jumpTo,
    skipIntro,
    restart,
    removeLayer,
    registerVideo,
    onVideoEnded,
    onShotError,
    /** The opening card finished fading out. */
    introGone: useCallback(() => setIntroLeaving(false), []),
    /** Current film position in seconds, for watch-history saving. */
    positionSeconds: () => {
      if (indexRef.current === INTRO_INDEX) return 0;
      const list = shotsRef.current;
      let before = 0;
      for (let j = 0; j < indexRef.current && j < list.length; j++) before += list[j].holdMs;
      return (before + Math.max(0, elapsedRef.current)) / 1000;
    },
  };
}

function videoLengthMs(el: HTMLVideoElement | undefined, shot: Shot): number {
  if (shot.item.trim) return shot.item.trim.keptSeconds * 1000;
  if (el && Number.isFinite(el.duration) && el.duration > 0) return el.duration * 1000;
  return shot.holdMs;
}
